import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';

/**
 * Listens for Capacitor appUrlOpen events (Universal Links on iOS).
 *
 * DORMANT BY DESIGN as of build 2170. Shared invite links are meant to open the
 * web app, so the iOS app claims no associated domain and the site serves no
 * apple-app-site-association — which means iOS never hands an https link to the
 * app and these handlers never fire. The routing below is kept intact so that
 * restoring the entitlement and the AASA file is all it takes to switch
 * universal links back on; see ios/App/App/App.entitlements.
 *
 * If it did fire: tapping https://afroyap-8bfb1.web.app/tribes/{id}?invite={code}
 * (or the /forums/{id} equivalent) navigates to the public invite page, which
 * handles auth state internally.
 *
 * Only active on native platforms — no-ops in the web browser.
 */
export function useDeepLinks() {
    const navigate = useNavigate();

    useEffect(() => {
        if (!Capacitor.isNativePlatform()) return;

        let removeListener: (() => void) | undefined;

        (async () => {
            try {
                const { App } = await import('@capacitor/app');

                const handle = await App.addListener('appUrlOpen', ({ url }) => {
                    try {
                        const parsed = new URL(url);
                        const pathname = parsed.pathname;

                        // /rooms/:roomId  — public room invite page. No invite code:
                        // a room is capped and short-lived, so the id is the invite.
                        const roomInviteMatch = pathname.match(/^\/rooms\/([^/]+)$/);
                        if (roomInviteMatch) {
                            navigate(`/rooms/${roomInviteMatch[1]}`, { replace: false });
                            return;
                        }

                        // /tribes/:tribeId  — public invite page (handles auth internally)
                        const tribeInviteMatch = pathname.match(/^\/tribes\/([^/]+)$/);
                        if (tribeInviteMatch) {
                            const tribeId = tribeInviteMatch[1];
                            const inviteCode = parsed.searchParams.get('invite') || '';
                            navigate(`/tribes/${tribeId}${inviteCode ? `?invite=${inviteCode}` : ''}`, { replace: false });
                            return;
                        }

                        // /tribe/:tribeId  — direct protected route (user already logged in)
                        const tribeDirectMatch = pathname.match(/^\/tribe\/([^/]+)$/);
                        if (tribeDirectMatch) {
                            const tribeId = tribeDirectMatch[1];
                            const inviteCode = parsed.searchParams.get('invite') || '';
                            navigate(`/tribe/${tribeId}${inviteCode ? `?invite=${inviteCode}` : ''}`, { replace: false });
                            return;
                        }

                        // /forums/:forumId  — public invite page (handles auth internally)
                        const forumInviteMatch = pathname.match(/^\/forums\/([^/]+)$/);
                        if (forumInviteMatch) {
                            const forumId = forumInviteMatch[1];
                            const inviteCode = parsed.searchParams.get('invite') || '';
                            navigate(`/forums/${forumId}${inviteCode ? `?invite=${inviteCode}` : ''}`, { replace: false });
                            return;
                        }

                        // /forum/:forumId  — direct protected route (user already logged in)
                        const forumDirectMatch = pathname.match(/^\/forum\/([^/]+)$/);
                        if (forumDirectMatch) {
                            const forumId = forumDirectMatch[1];
                            const inviteCode = parsed.searchParams.get('invite') || '';
                            navigate(`/forum/${forumId}${inviteCode ? `?invite=${inviteCode}` : ''}`, { replace: false });
                            return;
                        }

                        // /profile/:userId
                        const profileMatch = pathname.match(/^\/profile\/([^/]+)$/);
                        if (profileMatch) {
                            navigate(pathname, { replace: false });
                            return;
                        }

                        // /yap/:yapId
                        const yapMatch = pathname.match(/^\/yap\/([^/]+)$/);
                        if (yapMatch) {
                            navigate(pathname, { replace: false });
                            return;
                        }
                    } catch (e) {
                        console.error('[deepLinks] URL parse error:', e);
                    }
                });

                removeListener = () => handle.remove();
            } catch (e) {
                console.error('[deepLinks] listener setup failed:', e);
            }
        })();

        return () => { removeListener?.(); };
    }, [navigate]);
}
